import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import signatureSets from '../data/signatureSets';
import { useCart } from '../contexts/CartContext';
import Lightbox from '../components/Lightbox';
import Footer from './Footer';
import { Helmet } from 'react-helmet';
import { formatPrice } from '../utils/money';
import { imgSrc } from '../utils/img';
import '../pages/ProductDetail.css'

const allFilled = (arr) => arr.every((v) => v?.toString().trim() !== '');
const validateMeasurements = (m) => allFilled(m.L) && allFilled(m.R);

const SignatureDetail = () => {

  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const item = useMemo(() => signatureSets.find((s) => s.slug === slug), [slug]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedShape, setSelectedShape] = useState('');
  const [selectedLength, setSelectedLength] = useState('');
  const [errors, setErrors] = useState({});

  const [formState, setFormState] = useState({
    sizeType: '',
    customMode: 'entry',
    measurements: { L: ['', '', '', '', ''], R: ['', '', '', '', ''] },
  });

  if (!item) return <div className="not-found">Signature set not found.</div>;

  const priceLabel = formatPrice(item.priceCents);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!selectedShape) newErrors.shape = 'Please select a shape.';
    if (!selectedLength) newErrors.length = 'Please select a length.';
    if (!formState.sizeType) {
      newErrors.sizes = 'Please choose a size.';
    } else if (formState.sizeType === 'Custom' && formState.customMode === 'entry') {
      if (!validateMeasurements(formState.measurements)) {
        newErrors.sizes = 'Please enter all 10 measurements (left & right hand).';
      }
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    addToCart({
      slug: `signature/${item.slug}`,
      title: `Signature — ${item.title}`,
      price: priceLabel,
      isSignature: true,
      image: item.thumbnail,
      shape: selectedShape,
      length: selectedLength,
      sizes: {
        sizeType: formState.sizeType,
        customMode: formState.customMode,
        measurements: formState.sizeType === 'Custom' ? formState.measurements : null,
      },
      quantity: 1,
    });

    navigate('/nails/cart');
  };

  return (
    <>
      <Helmet>
        <title>{`Signature — ${item.title} | NeNeNail’dIt`}</title>
        <meta
          name="description"
          content={`Signature recreatable set — ${item.title}. Recreated exactly as shown. Choose your shape & length.`}
        />
      </Helmet>

      <div className="product-detail-wrapper">
        <div className="product-detail-page">
          <div className="aspect-[4/3] w-full overflow-hidden rounded mb-4">
            <img
              src={imgSrc(item.thumbnail)}
              alt={item.title}
              className="w-full h-full object-cover"
              onClick={() => setLightboxOpen(true)}
              loading="lazy"
              decoding="async"
              onError={(e) => (e.currentTarget.src = '/nails-preview-placeholder.jpg')}
            />

          </div>
          {lightboxOpen && (
            <Lightbox
              src={imgSrc(item.thumbnail)}
              alt={item.title}
              onClose={() => setLightboxOpen(false)}
            />
          )}
          <h1>{item.title}</h1>
          <p className="price">{priceLabel}</p>
          <p className="description">
            NeNe Nail’d It Again — iconic, art-heavy design recreated exactly as shown.
            Only shape, length, and fit can be customized.
          </p>

          <form className="product-form" onSubmit={handleSubmit}>
            {/* Shape */}
            <div className="form-group">
              <label>Shape <span className="text-red-500">*</span></label>
              <div className="shape-options">
                {(item.options?.shapes ?? ['Square', 'Almondetto', 'Coffin']).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`shape-btn ${selectedShape === s ? 'active' : ''}`}
                    onClick={() => setSelectedShape(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {errors.shape && <p className="text-red-600 text-sm mt-1">{errors.shape}</p>}
            </div>

            {/* Length */}
            <div className="form-group">
              <label>Length <span className="text-red-500">*</span></label>
              <div className="length-options">
                {(item.options?.lengths ?? ['Short', 'Medium', 'Long']).map((l) => (
                  <button
                    key={l}
                    type="button"
                    className={`length-btn ${selectedLength === l ? 'active' : ''}`}
                    onClick={() => setSelectedLength(l)}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {errors.length && <p className="text-red-600 text-sm mt-1">{errors.length}</p>}
            </div>

            {/* Sizes (dropdown + optional custom) */}
            <div className="form-group">
              <label>Sizes <span className="text-red-500">*</span></label>
              <select
                value={formState.sizeType}
                onChange={(e) => setFormState((s) => ({ ...s, sizeType: e.target.value }))}
                className="border border-gray-300 rounded-md p-2 w-full"
              >
                <option value="">-- Select a size --</option>
                <option value="Small">Small</option>
                <option value="Medium">Medium</option>
                <option value="Large">Large</option>
                <option value="Custom">Custom</option>
              </select>

              {formState.sizeType === 'Custom' && (
                <div className="mt-3 border rounded-md p-3 space-y-3">
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={formState.customMode === 'entry'}
                        onChange={() => setFormState((s) => ({ ...s, customMode: 'entry' }))}
                      />
                      Enter my measurements
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={formState.customMode === 'fitting'}
                        onChange={() => setFormState((s) => ({ ...s, customMode: 'fitting' }))}
                      />
                      In-person fitting (pop-up)
                    </label>
                  </div>

                  {formState.customMode === 'entry' ? (
                    <>
                      <p className="text-sm text-gray-600">
                        Measure each finger in mm (Left & Right hands).
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        {['L', 'R'].map((hand) => (
                          <div key={hand}>
                            <div className="text-xs font-medium mb-2">
                              {hand === 'L' ? 'Left' : 'Right'} hand — Thumb → Pinky
                            </div>
                            <div className="grid grid-cols-5 gap-2">
                              {['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'].map((label, i) => (
                                <input
                                  key={`${hand}-${i}`}
                                  type="number"
                                  min={8}
                                  max={25}
                                  placeholder={label}
                                  className="border rounded p-2 w-full"
                                  value={formState.measurements[hand][i]}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setFormState((s) => {
                                      const copy = { ...s.measurements, [hand]: [...s.measurements[hand]] };
                                      copy[hand][i] = v;
                                      return { ...s, measurements: copy };
                                    });
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">
                      We’ll size you in person at the pop-up — you’ll hand your size card to NeNe.
                    </p>
                  )}
                </div>
              )}
              {errors.sizes && <p className="text-red-600 text-sm mt-2">{errors.sizes}</p>}
            </div>

            {/* Preview */}
            {(selectedShape || selectedLength) && (
              <div className="preview-box">
                <h4>Your Choices:</h4>
                <ul>
                  {selectedShape && <li><strong>Shape:</strong> {selectedShape}</li>}
                  {selectedLength && <li><strong>Length:</strong> {selectedLength}</li>}
                  {formState.sizeType && (
                    <li>
                      <strong>Size:</strong> {formState.sizeType}
                      {formState.sizeType === 'Custom' && (
                        <> — {formState.customMode === 'entry' ? 'Measurements provided' : 'In-person fitting'}</>
                      )}
                    </li>
                  )}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-4 mt-6">
              <button
                type="submit"
                className="bg-[#e6e68c] text-[#e86ba5] font-semibold rounded-xl px-5 py-2 shadow-md hover:shadow-lg hover:scale-105 transition-all"
              >
                Add to Cart
              </button>

              <Link
                to="/nails"
                className="underline text-[#e86ba5] text-sm hover:text-[#c74f86] transition-colors"
              >
                Continue browsing
              </Link>
            </div>


          </form>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SignatureDetail;
